import { api } from './api';
import { idbCache } from './idb';

/**
 * 任务数据的离线缓存。
 *
 * ★ 写操作有 outbox 兜底,读操作却没有 —— 司机在地下仓库打开「取货」,
 *   看到的是「加载失败」四个字,活干不下去。**写能离线、读不能离线的
 *   离线支持是残缺的**。
 *
 * 策略是最朴素的 stale-while-offline:
 *   在线 → 请求网络,成功后落缓存
 *   离线/失败 → 回退到上一次的缓存,并明确告诉司机这是旧数据
 *
 * 不做 stale-while-revalidate(先给缓存再后台刷新)—— 任务状态变了
 * 界面却还显示旧的,比多等 200ms 危险得多。
 */

const PREFIX = 'cache:';
/** 超过这个时长的缓存不再使用:隔夜的任务列表基本全是错的,不如明说没有数据 */
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

interface CacheEntry<T> {
  id: string;
  cachedAt: number;
  payload: T;
}

export interface CachedResult<T> {
  data: T;
  /** true = 网络失败,这是本地旧数据 */
  stale: boolean;
  /** 缓存写入时间,stale 时用于告诉司机「更新于几点」 */
  cachedAt: number | null;
}

export async function cachedGet<T>(
  path: string,
  /**
   * 这条路径没缓存时退而求其次去找的那一条。
   *
   * ★ 为「同一份数据、不同查询参数」准备的:缓存是按**完整路径**存的,
   *   所以 `/driver/tasks` 和 `/driver/tasks?includeFuture=1` 是两条记录。
   *   司机在地下室拨一下「非当日达」,新那条从来没被缓存过 ——
   *   于是**整个任务列表变成一句报错**,而他手上的活一件都没变。
   *
   * ★ 退回去拿到的是「窄」的那一份(比如只有今天的单)。
   *   那是诚实的:没下载过的单本来就不在这台手机上。
   *   少几单也远好过一屏红字 —— 他此刻要的是「我还有哪几单要送」。
   */
  fallbackPath?: string,
): Promise<CachedResult<T>> {
  const id = PREFIX + path;

  try {
    const data = await api<T>(path);
    // 缓存写失败(隐私模式下 IndexedDB 可能不可用)不该影响正常请求
    await idbCache.put<CacheEntry<T>>({ id, cachedAt: Date.now(), payload: data }).catch(() => {});
    return { data, stale: false, cachedAt: Date.now() };
  } catch (networkError) {
    const fresh = (e?: CacheEntry<T>): e is CacheEntry<T> =>
      !!e && Date.now() - e.cachedAt <= MAX_AGE_MS;

    const hit = await idbCache.get<CacheEntry<T>>(id).catch(() => undefined);
    if (fresh(hit)) return { data: hit.payload, stale: true, cachedAt: hit.cachedAt };

    if (fallbackPath && fallbackPath !== path) {
      const alt = await idbCache
        .get<CacheEntry<T>>(PREFIX + fallbackPath)
        .catch(() => undefined);
      if (fresh(alt)) return { data: alt.payload, stale: true, cachedAt: alt.cachedAt };
    }

    // 两条都没有时把原始错误抛回去 —— 让调用方显示真实原因,
    // 而不是一句含糊的「加载失败」
    throw networkError;
  }
}

/** 退出登录时清掉 —— 任务数据里有收件人姓名电话,不能留给下一个登录的人 */
export async function clearCache(): Promise<void> {
  await idbCache.clear().catch(() => {});
}
